import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Loader2, Save, RotateCcw, Check, X, Info } from 'lucide-react';
import { rolePermissionsAPI } from '../../services/api';
import { invalidatePermissionsCache } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const MODULE_COLORS = {
  'Order Confirmations': { bg: '#7B1FA214', text: '#7B1FA2', border: '#7B1FA230' },
  'Purchase': { bg: '#1565C014', text: '#1565C0', border: '#1565C030' },
  'Vendors': { bg: '#00695C14', text: '#00695C', border: '#00695C30' },
  'Quotations': { bg: '#E6510014', text: '#E65100', border: '#E6510030' },
  'Sales': { bg: '#AD143414', text: '#AD1434', border: '#AD143430' },
  'Sales Returns': { bg: '#C2185B14', text: '#C2185B', border: '#C2185B30' },
  'Reports': { bg: '#00897B14', text: '#00897B', border: '#00897B30' },
  'Accounts': { bg: '#4A148C14', text: '#4A148C', border: '#4A148C30' },
  'Settings': { bg: '#37474F14', text: '#37474F', border: '#37474F30' },
};

const ROLE_COLORS = {
  'Admin': { bg: '#dc262614', text: '#dc2626' },
  'Manager': { bg: '#2563eb14', text: '#2563eb' },
  'Salesman': { bg: '#16a34a14', text: '#16a34a' },
  'Operator': { bg: '#ea580c14', text: '#ea580c' },
  'Accounts': { bg: '#7c3aed14', text: '#7c3aed' },
  'Branch Incharge': { bg: '#0891b214', text: '#0891b2' },
  'Logistics': { bg: '#ca8a0414', text: '#ca8a04' },
};

const RolePermissionsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [definitions, setDefinitions] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [updatedAt, setUpdatedAt] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [originalPermissions, setOriginalPermissions] = useState({});

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await rolePermissionsAPI.get();
      setPermissions(data.permissions || {});
      setOriginalPermissions(JSON.parse(JSON.stringify(data.permissions || {})));
      setDefinitions(data.definitions || []);
      setAllRoles(data.all_roles || []);
      setUpdatedAt(data.updated_at || '');
      setUpdatedBy(data.updated_by || '');
      setHasChanges(false);
    } catch (err) {
      toast.error('Failed to load role permissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  const togglePermission = (permKey, role) => {
    if (role === 'Admin') return; // Admin always has all permissions
    const current = permissions[permKey] || [];
    const updated = current.includes(role)
      ? current.filter(r => r !== role)
      : [...current, role];
    const newPerms = { ...permissions, [permKey]: updated };
    setPermissions(newPerms);
    setHasChanges(JSON.stringify(newPerms) !== JSON.stringify(originalPermissions));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await rolePermissionsAPI.update(permissions);
      setPermissions(data.permissions || {});
      setOriginalPermissions(JSON.parse(JSON.stringify(data.permissions || {})));
      setUpdatedAt(data.updated_at || '');
      setUpdatedBy(data.updated_by || '');
      setHasChanges(false);
      invalidatePermissionsCache();
      toast.success('Role permissions saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all permissions to factory defaults? This cannot be undone.')) return;
    setSaving(true);
    try {
      const data = await rolePermissionsAPI.reset();
      setPermissions(data.permissions || {});
      setOriginalPermissions(JSON.parse(JSON.stringify(data.permissions || {})));
      setUpdatedAt(data.updated_at || '');
      setUpdatedBy(data.updated_by || '');
      setHasChanges(false);
      invalidatePermissionsCache();
      toast.success('Permissions reset to defaults');
    } catch (err) {
      toast.error('Failed to reset permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
    setHasChanges(false);
  };

  // Group definitions by module
  const moduleGroups = definitions.reduce((acc, def) => {
    if (!acc[def.module]) acc[def.module] = [];
    acc[def.module].push(def);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="role-permissions-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>
            Role Permissions
          </h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Configure which roles can perform specific actions across modules
          </p>
          {updatedAt && (
            <p className="text-[10px] text-gray-400 mt-0.5">
              Last updated: {new Date(updatedAt).toLocaleString()} by {updatedBy}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <button onClick={handleDiscard} data-testid="discard-permissions-btn"
              className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all">
              <X className="w-3.5 h-3.5" /> Discard
            </button>
          )}
          <button onClick={handleReset} data-testid="reset-permissions-btn"
            className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-all">
            <RotateCcw className="w-3.5 h-3.5" /> Reset Defaults
          </button>
          <button onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-permissions-btn"
            className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold text-white rounded-lg transition-all ${
              hasChanges ? 'shadow-md hover:shadow-lg' : 'opacity-50 cursor-not-allowed'
            }`}
            style={{ backgroundColor: '#24549c' }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-2.5 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
        <div className="text-[11px] text-blue-700 leading-relaxed">
          <strong>Admin</strong> role always has full access and cannot be unchecked.
          Changes take effect immediately after saving. The OC workflow steps, purchase orders,
          vendor management, and other module actions are all controlled here.
        </div>
      </div>

      {/* Role Legend */}
      <div className="flex flex-wrap gap-2">
        {allRoles.map(role => {
          const colors = ROLE_COLORS[role] || { bg: '#94a3b814', text: '#64748b' };
          return (
            <span key={role} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold"
              style={{ backgroundColor: colors.bg, color: colors.text }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.text }} />
              {role}
            </span>
          );
        })}
      </div>

      {/* Permission Matrix by Module */}
      {Object.entries(moduleGroups).map(([moduleName, defs]) => {
        const colors = MODULE_COLORS[moduleName] || { bg: '#64748b14', text: '#64748b', border: '#64748b30' };
        return (
          <div key={moduleName} className="bg-white rounded-xl border border-gray-100 overflow-hidden"
            data-testid={`permission-module-${moduleName.toLowerCase().replace(/\s+/g, '-')}`}>
            {/* Module Header */}
            <div className="px-4 py-3 border-b border-gray-50" style={{ backgroundColor: colors.bg }}>
              <h4 className="text-xs font-bold" style={{ color: colors.text }}>{moduleName}</h4>
            </div>

            {/* Permission Grid */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-50">
                    <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[280px]">
                      Action
                    </th>
                    {allRoles.map(role => (
                      <th key={role} className="px-2 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[90px]">
                        {role === 'Branch Incharge' ? 'Br. Incharge' : role}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {defs.map((def, idx) => {
                    const permRoles = permissions[def.key] || [];
                    return (
                      <tr key={def.key}
                        className={idx < defs.length - 1 ? 'border-b border-gray-50' : ''}
                        data-testid={`permission-row-${def.key}`}>
                        <td className="px-4 py-2.5">
                          <div className="text-xs font-semibold text-gray-800">{def.label}</div>
                          <div className="text-[10px] text-gray-400 mt-0.5">{def.description}</div>
                        </td>
                        {allRoles.map(role => {
                          const isEnabled = role === 'Admin' || permRoles.includes(role);
                          const isAdmin = role === 'Admin';
                          const roleColor = ROLE_COLORS[role] || { text: '#64748b' };
                          return (
                            <td key={role} className="px-2 py-2.5 text-center">
                              <button
                                onClick={() => togglePermission(def.key, role)}
                                disabled={isAdmin}
                                data-testid={`perm-toggle-${def.key}-${role.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`w-7 h-7 rounded-lg flex items-center justify-center mx-auto transition-all ${
                                  isAdmin
                                    ? 'bg-gray-100 cursor-not-allowed'
                                    : isEnabled
                                    ? 'hover:opacity-80 cursor-pointer shadow-sm'
                                    : 'bg-gray-50 hover:bg-gray-100 cursor-pointer'
                                }`}
                                style={isEnabled ? { backgroundColor: roleColor.text + '18' } : {}}
                                title={isAdmin ? 'Admin always has access' : `Toggle ${role} for ${def.label}`}>
                                {isEnabled ? (
                                  <Check className="w-3.5 h-3.5" style={{ color: isAdmin ? '#9ca3af' : roleColor.text }} />
                                ) : (
                                  <X className="w-3 h-3 text-gray-300" />
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Unsaved Changes Bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-amber-50 border-t-2 border-amber-400 px-6 py-3 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-xs font-semibold text-amber-800">You have unsaved changes</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleDiscard}
              className="px-3 py-1.5 text-[11px] font-semibold text-gray-600 bg-white rounded-lg border border-gray-200 hover:bg-gray-50">
              Discard
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-1.5 text-[11px] font-semibold text-white rounded-lg"
              style={{ backgroundColor: '#24549c' }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolePermissionsTab;
